import { RightOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Space, Switch, TimePicker } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import {
  getAdminTelegramTypeKeys,
  getClientTelegramTypeKeys,
  TELEGRAM_TYPE_LABELS,
} from '../../utils/cabinetMarketingUtils';
import TelegramTypeTemplateFields from './TelegramTypeTemplateFields';

const TelegramTypeRow = ({
  typeKey,
  cabinetTelegram,
  patchCabinetTelegram,
  onDirty,
  showNotifyManager,
}) => {
  const [expanded, setExpanded] = useState(false);
  const typeLabel = TELEGRAM_TYPE_LABELS[typeKey];

  const handleToggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const handleToggleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    handleToggleExpanded();
  };

  const handleToggleEnabled = (checked) => {
    patchCabinetTelegram((p) => ({
      ...p,
      types: {
        ...p.types,
        [typeKey]: { ...p.types[typeKey], enabled: checked },
      },
    }));
    onDirty();
  };

  const handleToggleNotifyManager = (checked) => {
    patchCabinetTelegram((p) => ({
      ...p,
      types: {
        ...p.types,
        [typeKey]: { ...p.types[typeKey], notify_manager: checked },
      },
    }));
    onDirty();
  };

  return (
    <div className="sett-telegram-type-row">
      <div className="sett-telegram-type-row__header">
        <Switch
          checked={cabinetTelegram.types[typeKey]?.enabled !== false}
          onChange={handleToggleEnabled}
          aria-label={typeLabel}
        />
        <button
          type="button"
          className="sett-telegram-type-row__toggle"
          onClick={handleToggleExpanded}
          onKeyDown={handleToggleKeyDown}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Свернуть' : 'Развернуть'} шаблон: ${typeLabel}`}
          tabIndex={0}
        >
          <RightOutlined
            className={`sett-telegram-type-row__chevron${expanded ? ' sett-telegram-type-row__chevron--expanded' : ''}`}
            aria-hidden
          />
          <strong className="sett-telegram-type-row__title">{typeLabel}</strong>
        </button>
        {showNotifyManager ? (
          <Checkbox
            checked={cabinetTelegram.types[typeKey]?.notify_manager === true}
            onChange={(e) => handleToggleNotifyManager(e.target.checked)}
            aria-label={`${typeLabel} — отправлять менеджеру`}
          >
            Отправлять менеджеру
          </Checkbox>
        ) : null}
      </div>
      {expanded ? (
        <div className="sett-telegram-type-row__body">
          <TelegramTypeTemplateFields
            typeKey={typeKey}
            cabinetTelegram={cabinetTelegram}
            patchCabinetTelegram={patchCabinetTelegram}
            onDirty={onDirty}
          />
          {typeKey === 'admin_commerce' ? (
            <div className="sett-cell--muted sett-telegram-type-row__hint">
              Плейсхолдеры: {'{client_login}'}, {'{proxy_kind_phrase}'}, {'{qty}'}, {'{amount_rub}'}; для
              пополнения баланса — login и сумма.
            </div>
          ) : null}
          {typeKey === 'admin_periodic_report' ? (
            <div className="sett-cell--muted sett-telegram-type-row__hint">
              В 00:10 MSK: ежедневный отчёт за вчера; по понедельникам — за пн–вс; 1-го числа — за прошлый
              месяц. Текст отчёта фиксированный (регистрации, покупки, остаток в продаже СПб/Москва).
            </div>
          ) : null}
          {typeKey === 'regional_restrictions' ? (
            <div className="sett-cell--muted sett-telegram-type-row__hint">
              Плейсхолдер: {'{region}'} — название региона (город).
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const SaveButton = ({ sectionDirty, sectionSaving, onSave }) => (
  <Button
    type={sectionDirty ? 'primary' : 'default'}
    disabled={!sectionDirty}
    loading={sectionSaving}
    onClick={onSave}
    aria-label="Сохранить уведомления Telegram"
  >
    Сохранить
  </Button>
);

const CabinetTelegramNotificationsTab = ({
  sectionDirty,
  sectionSaving,
  handleSaveMarketingSection,
  cabinetTelegram,
  patchCabinetTelegram,
  handleTelegramDirty,
}) => {
  const handleNotificationsDirty = () => handleTelegramDirty('notifications');
  const handleSave = () => handleSaveMarketingSection('notifications');
  const clientTypeKeys = getClientTelegramTypeKeys();
  const adminTypeKeys = getAdminTelegramTypeKeys();

  const handlePatchShared = (patch) => {
    patchCabinetTelegram((p) => ({ ...p, ...patch }));
    handleNotificationsDirty();
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div className="sett-card sett-telegram-notifications-card">
        <div className="sett-card__header sett-card__header--row">
          <h3 className="sett-card__title">Уведомления Telegram — Клиентам</h3>
          <SaveButton
            sectionDirty={sectionDirty.notifications}
            sectionSaving={sectionSaving.notifications}
            onSave={handleSave}
          />
        </div>
        <div className="sett-card__body">
          <p className="sett-cell--muted" style={{ marginBottom: 12 }}>
            Внешний бот: переменная окружения TELEGRAM_SERVICE_TOKEN, заголовок X-Telegram-Service-Token. Клиенты
            получают сообщения, если привязан telegram_id (вход через Telegram или ссылка из настроек ЛК).
          </p>
          <p className="sett-cell--muted" style={{ marginBottom: 12 }}>
            При первом включении уже накопившаяся очередь не отправляется: старые pending-сообщения будут сброшены,
            бот начнёт слать только новые события.
          </p>
          <p className="sett-cell--muted" style={{ marginBottom: 12 }}>
            Плейсхолдеры: {'{first_name}'}, {'{proxy_lines}'}, {'{expires_summary}'}, {'{grace_hours}'}, {'{grace_days}'},{' '}
            {'{region}'}, {'{amount_rub}'}, {'{lk_url}'}, {'{payment_link}'}, {'{client_id}'}, {'{client_login}'},{' '}
            {'{tariff_summary}'}, {'{proxy_qty}'}, {'{order_public_id}'}, {'{alert_detail}'},{' '}
            {'{manager_client_url}'}.
          </p>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space align="center" wrap>
              <Switch
                checked={!!cabinetTelegram.enabled}
                onChange={(c) => handlePatchShared({ enabled: c })}
                aria-label="Включить уведомления Telegram"
              />
              <span>Включить уведомления</span>
            </Space>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ minWidth: 200, flex: '1 1 200px' }}>
                <div className="sett-cell--muted" style={{ marginBottom: 4 }}>
                  Часовой пояс
                </div>
                <Input
                  value={cabinetTelegram.timezone}
                  onChange={(e) => handlePatchShared({ timezone: e.target.value })}
                  aria-label="Часовой пояс"
                />
              </div>
              <div>
                <div className="sett-cell--muted" style={{ marginBottom: 4 }}>
                  Время ежедневных напоминаний
                </div>
                <TimePicker
                  format="HH:mm"
                  value={
                    cabinetTelegram.daily_send_time
                      ? dayjs(cabinetTelegram.daily_send_time, 'HH:mm')
                      : null
                  }
                  onChange={(t) => {
                    const v = t && t.isValid() ? t.format('HH:mm') : '10:00';
                    handlePatchShared({ daily_send_time: v });
                  }}
                  aria-label="Время напоминаний о продлении"
                />
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ flex: '2 1 280px', minWidth: 200 }}>
                <div className="sett-cell--muted" style={{ marginBottom: 4 }}>
                  Имя бота
                </div>
                <Input
                  value={cabinetTelegram.bot_username}
                  onChange={(e) => handlePatchShared({ bot_username: e.target.value })}
                  aria-label="Имя Telegram-бота"
                />
              </div>
              <div style={{ flex: '2 1 280px', minWidth: 200 }}>
                <div className="sett-cell--muted" style={{ marginBottom: 4 }}>
                  Публичный URL ЛК
                </div>
                <Input
                  value={cabinetTelegram.lk_public_url}
                  onChange={(e) => handlePatchShared({ lk_public_url: e.target.value })}
                  aria-label="URL личного кабинета"
                />
              </div>
            </div>
            {clientTypeKeys.map((typeKey) => (
              <TelegramTypeRow
                key={typeKey}
                typeKey={typeKey}
                cabinetTelegram={cabinetTelegram}
                patchCabinetTelegram={patchCabinetTelegram}
                onDirty={handleNotificationsDirty}
                showNotifyManager={false}
              />
            ))}
          </Space>
        </div>
      </div>

      <div className="sett-card sett-telegram-admin-notifications-card">
        <div className="sett-card__header sett-card__header--row">
          <h3 className="sett-card__title">Уведомления Telegram — Админам</h3>
          <SaveButton
            sectionDirty={sectionDirty.notifications}
            sectionSaving={sectionSaving.notifications}
            onSave={handleSave}
          />
        </div>
        <div className="sett-card__body">
          <div style={{ marginBottom: 16 }}>
            <div className="sett-cell--muted" style={{ marginBottom: 4 }}>
              База ссылки клиентов в админке
            </div>
            <Input
              value={cabinetTelegram.manager_clients_url_base}
              onChange={(e) => handlePatchShared({ manager_clients_url_base: e.target.value })}
              aria-label="URL списка клиентов в менеджере"
            />
          </div>
          {adminTypeKeys.map((typeKey) => (
            <TelegramTypeRow
              key={typeKey}
              typeKey={typeKey}
              cabinetTelegram={cabinetTelegram}
              patchCabinetTelegram={patchCabinetTelegram}
              onDirty={handleNotificationsDirty}
              showNotifyManager
            />
          ))}
        </div>
      </div>
    </Space>
  );
};

export default CabinetTelegramNotificationsTab;
